import React from 'react';
import { QStack, QBox, QFlex } from '../../QLayouts';
import { QLink, QSpinner, QTooltip } from '../../QAtoms';
import { QIcon } from '../../QIcons';
import { SearchDomain, SearchResource } from '../../types/domains/Search';
import { IconNameType } from '../../types/Icons';
import { DataProps } from '../../utils';
import { SkeletonText } from '@chakra-ui/react';

type domainOrResourceType = SearchDomain | SearchResource;

/**
 * Combined mapping for domain/resource types containing both icon names and display labels
 */
export const domainOrResourceMapping: {
  [key in domainOrResourceType]: {
    icon: IconNameType;
    label: string;
  };
} = {
  // resourceTypes
  'change-control': { icon: 'ResourceCR', label: 'Change control' },
  'change-request': { icon: 'ResourceCR', label: 'Change request' },
  'compliance-control': {
    icon: 'ResourceControl',
    label: 'Compliance control',
  },
  'compliance-framework': {
    icon: 'CheckCircle',
    label: 'Compliance framework',
  },
  'compliance-gap': {
    icon: 'ResourceComplianceGap',
    label: 'Compliance gap',
  },
  'dc-requirement': {
    icon: 'ResourceDesignControls',
    label: 'Design controls requirement',
  },
  'dc-testCase': {
    icon: 'ResourceDesignControls',
    label: 'Design controls test case',
  },
  'dc-change-control': {
    icon: 'ResourceDesignControls',
    label: 'Design controls review',
  },
  'dc-risk': { icon: 'ResourceDesignControls', label: 'Design controls risk' },
  'design-controls-configuration': {
    icon: 'ResourceDesignControls',
    label: 'Design controls product',
  },
  document: { icon: 'ResourceDocuments', label: 'Document' },
  'document-template': {
    icon: 'ResourceDocumentTemplates',
    label: 'Document template',
  },
  'quality-event': { icon: 'ResourceEvents', label: 'Event' },
  'event-template': { icon: 'ResourceEventTemplates', label: 'Event template' },
  'registry-item': { icon: 'ResourceRegistry', label: 'Resource library' },
  'registry-category': {
    icon: 'ResourceRegistry',
    label: 'Resource library template',
  },
  supplier: { icon: 'ResourceSuppliers', label: 'Supplier' },
  'supplier-configuration': {
    icon: 'ResourceSuppliers',
    label: 'Supplier configuration',
  },
  'training-plan': { icon: 'ResourceTrainingPlans', label: 'Training plan' },
  user: { icon: 'ResourceUsers', label: 'User' },
  'compliance-control-task': {
    icon: 'ResourceTask',
    label: 'Compliance task',
  },
  'quality-event-task': {
    icon: 'ResourceTask',
    label: 'Event task',
  },
  task: {
    icon: 'ResourceTask',
    label: 'Task',
  },
  // domains
  'change-management': { icon: 'ResourceCR', label: 'Change control' },
  'change-requests': { icon: 'ResourceCR', label: 'Change request' },
  'design-controls': {
    icon: 'ResourceDesignControls',
    label: 'Design controls',
  },
  documents: { icon: 'ResourceDocuments', label: 'Document' },
  'quality-events': { icon: 'ResourceEvents', label: 'Event' },
  registry: { icon: 'ResourceRegistry', label: 'Resource library' },
  suppliers: { icon: 'ResourceSuppliers', label: 'Supplier' },
  training: { icon: 'ResourceTrainingPlans', label: 'Training' },
  users: { icon: 'ResourceUsers', label: 'User' },
  compliance: {
    icon: 'ResourceControl',
    label: 'Compliance',
  },
  tasks: {
    icon: 'ResourceTask',
    label: 'Task',
  },
};

/**
 * Map of domain/resource types to their corresponding icon names
 * Maintained for backwards compatibility where this mapping is used.
 */
export const domainOrResourceIconMap = Object.fromEntries(
  Object.entries(domainOrResourceMapping).map(([key, value]) => [
    key,
    value.icon,
  ]),
) as { [key in domainOrResourceType]: IconNameType };

export type QResourceIconProps = {
  /** Either `domain` or `resourceType` values from the Common Resource Format can be used. `resourceType` is preferred. */
  domain: domainOrResourceType;
  isLoading?: boolean;
} & DataProps;

/**
 * This component displays an icon of a linked Qualio object e.g. Event, Document, Supplier, User.
 * This can be used independently from the `QResourceLabel`.
 */
export const QResourceIcon: React.FC<QResourceIconProps> = ({
  domain,
  ...dataProps
}) => (
  <QFlex>
    <QTooltip label={domainOrResourceMapping[domain].label} placement="top">
      <QIcon iconName={domainOrResourceMapping[domain].icon} {...dataProps} />
    </QTooltip>
  </QFlex>
);

export type QResourceLabelProps = {
  /** Either `domain` or `resourceType` values from the Common Resource Format can be used. `resourceType` is preferred. */
  domain: domainOrResourceType;
  link?: string;
  openInNewTab?: boolean;
  onClick?: () => void;
  isLoading?: boolean;
  children?: React.ReactNode;
} & DataProps;

/**
 * This component displays informations about linked Qualio object e.g. Event, Document, Supplier, User.
 */
export const QResourceLabel: React.FC<QResourceLabelProps> = ({
  domain,
  link,
  openInNewTab,
  children,
  onClick,
  isLoading = false,
  ...dataProps
}) => {
  if (isLoading) {
    return (
      <QStack direction="row" spacing="8px" align="center" {...dataProps}>
        <QSpinner size="sm" color="grey" opacity={0.5} />
        <SkeletonText
          noOfLines={1}
          width="100px"
          startColor="gray.200"
          endColor="gray.300"
        />
        {/* Hidden box to ensure the layout remains consistent while loading */}
        <QBox
          as="span"
          visibility="hidden"
          aria-hidden
          w={0}
          overflow="clip"
          pointerEvents="none"
          sx={{ 'text-wrap': 'nowrap' }}
        >
          {children}
        </QBox>
      </QStack>
    );
  }
  return (
    <QStack direction="row" spacing="8px" align="center" {...dataProps}>
      <QResourceIcon domain={domain} />
      <QBox fontSize="sm">
        {link === undefined ? (
          children
        ) : (
          <QLink isExternal={openInNewTab} href={link} onClick={onClick}>
            {children}
          </QLink>
        )}
      </QBox>
    </QStack>
  );
};
